import React from 'react'
import { Layout } from '../components/layout'
import { Meta } from '../components/meta'
import { useStoryblok } from '../hooks/useStoryblok'
import Page from '../components/page'

const PageTemplate = ({
  pageContext,
  location,
}: {
  pageContext: any
  location: Location
}) => {
  const { story } = pageContext
  const storyLive = useStoryblok(story, location)

  return (
    <Layout>
      <Page story={storyLive} location={location} />
    </Layout>
  )
}

export default PageTemplate

export const Head = ({ pageContext }: { pageContext: any }) => {
  const { story } = pageContext

  // Handle missing or invalid story data gracefully
  if (!story?.content) {
    return <Meta title="Milk & Honey Property Styling" />
  }

  let content
  try {
    content =
      typeof story.content === 'string'
        ? JSON.parse(story.content)
        : story.content
  } catch (error) {
    console.warn('Error parsing story content:', error)
    return <Meta title="Milk & Honey Property Styling" />
  }

  // Determine if this is a work/portfolio page
  const isWorkPage = story.full_slug?.startsWith('work/')

  // Generate structured data for portfolio pages
  let images: any[] = []
  let services: any[] = []

  if (isWorkPage && content.body) {
    // Extract images from the content body
    content.body.forEach((block: any) => {
      if (block.component === 'full-width-image' && block.image?.filename) {
        images.push({
          url: block.image.filename,
          alt: block.alt || content.title || '',
          caption: content.title,
        })
      }
      if (block.component === 'constrained-image' && block.image?.filename) {
        images.push({
          url: block.image.filename,
          alt: block.alt || content.title || '',
          caption: content.title,
        })
      }
      if (block.component === 'container' && block.children) {
        block.children.forEach((child: any) => {
          if (
            child.component === 'constrained-image' &&
            child.image?.filename
          ) {
            images.push({
              url: child.image.filename,
              alt: child.alt || content.title || '',
              caption: content.title,
            })
          }
        })
      }
      if (block.component === 'grid' && block.content) {
        block.content.forEach((item: any) => {
          if (item.component === 'work-listing' && item.thumbnail?.filename) {
            images.push({
              url: item.thumbnail.filename,
              alt: item.title || '',
              caption: item.title,
            })
          }
        })
      }
    })
  } // Define services for service-related pages
  if (isWorkPage) {
    services = [
      {
        name: 'Property Styling',
        description:
          'Professional interior styling for residential properties and real estate staging',
        provider: 'Milk & Honey Property Styling',
        areaServed: ['Melbourne', 'Victoria'],
        serviceType: 'Interior Design',
        image: content.openGraphImage?.filename,
      },
    ]
  }

  return (
    <Meta
      title={content.seoTitle || content.title}
      description={content.seoDescription || content.description}
      openGraphTitle={content.openGraphTitle || content.title}
      openGraphDescription={content.openGraphDescription || content.description}
      openGraphImage={content.openGraphImage?.filename}
      structuredDataType={isWorkPage ? 'ImageGallery' : 'LocalBusiness'}
      images={images.length > 0 ? images : undefined}
      services={services.length > 0 ? services : undefined}
      includeDefaultBusiness={!isWorkPage}
    />
  )
}
